gsap.registerPlugin(ScrollTrigger);

const cardsWrappers = gsap.utils.toArray(".service-stack-item");
const sectionWrap = document.querySelector(".service-stack-parent");
const headingWrap = document.querySelector(".service-stack-heading-wrap");
const stickButtons = document.querySelector(".stick-button-item");
const stickButtonsWrap = document.querySelector(".stick-buttons");
const serviceGradient = document.querySelector(".service-gradient");
const isServicesPageTemplate = document.body?.dataset?.suffix === "services";

const isLastCard = (i) => i === cardsWrappers.length - 1;

const CARD_BG_FROM = "#f5f5f5";
const CARD_BG_TO = "#ffffff";
const CARD_STACK_BASE_Z = 40;
const UI_TOP_LAYER = 2000;

const pinOptions = isServicesPageTemplate
  ? {
      pinType: "transform",
      anticipatePin: 0,
      invalidateOnRefresh: true,
    }
  : {
      invalidateOnRefresh: true,
    };

const mm = gsap.matchMedia();

function createTrigger(config) {
  const needsPinOptions = !!config.pin;
  return ScrollTrigger.create({
    ...(needsPinOptions ? pinOptions : {}),
    ...config,
  });
}

function applyPinnedLayer(self, element, layer) {
  if (!element) return;
  gsap.set(element, { position: "relative", zIndex: layer });
  if (self?.pin) gsap.set(self.pin, { position: "relative", zIndex: layer });
  if (self?.pinSpacer) gsap.set(self.pinSpacer, { position: "relative", zIndex: layer });
}

/* Heading pin + fade: desktop only (>=1024px). */
mm.add("(min-width: 1024px)", () => {
  const created = [];
  let headingFadeTween = null;

  if (headingWrap && sectionWrap) {
    const startValues = "top top";

    created.push(
      createTrigger({
        trigger: headingWrap,
        start: startValues,
        endTrigger: sectionWrap,
        end: "bottom top",
        pin: true,
        pinSpacing: false,
      })
    );

    headingFadeTween = gsap.to(headingWrap, {
      opacity: 0,
      ease: "none",
      paused: true,
    });

    created.push(
      createTrigger({
        trigger: headingWrap,
        start: startValues,
        end: () => `+=${window.innerHeight * 0.1}`,
        onUpdate(self) {
          headingFadeTween.progress(self.progress);
        },
      })
    );
  }

  return () => {
    created.forEach((st) => st.kill());
    if (headingFadeTween) {
      headingFadeTween.kill();
      gsap.set(headingWrap, { clearProps: "opacity" });
    }
  };
});

/* Sticky CTA: desktop/tablet = pin, always visible. Mobile = same pin window, show only while trigger is active (pin on). */
mm.add("(min-width: 768px)", () => {
  if (!stickButtons || !sectionWrap) return () => {};
  if (stickButtonsWrap) {
    gsap.set(stickButtonsWrap, { position: "relative", zIndex: UI_TOP_LAYER + 1 });
  }
  gsap.set(stickButtons, { zIndex: UI_TOP_LAYER });
  const stickyButtonsTrigger = createTrigger({
    trigger: stickButtons,
    start: "bottom bottom-=20",
    endTrigger: sectionWrap,
    end: "bottom top",
    pin: true,
    pinSpacing: false,
    onRefresh(self) {
      applyPinnedLayer(self, stickButtons, UI_TOP_LAYER);
    },
    onToggle(self) {
      applyPinnedLayer(self, stickButtons, UI_TOP_LAYER);
    },
  });
  if (stickyButtonsTrigger) {
    applyPinnedLayer(stickyButtonsTrigger, stickButtons, UI_TOP_LAYER);
  }
  return () => {
    stickyButtonsTrigger?.kill();
    gsap.set(stickButtons, { clearProps: "zIndex" });
    if (stickButtonsWrap) gsap.set(stickButtonsWrap, { clearProps: "position,zIndex" });
  };
});

mm.add("(max-width: 767px)", () => {
  if (!stickButtons || !sectionWrap) return () => {};
  if (stickButtonsWrap) {
    gsap.set(stickButtonsWrap, { position: "relative", zIndex: UI_TOP_LAYER + 1 });
  }

  const motionEl = stickButtons.querySelector(".stick-button-item-motion") || stickButtons;
  /** Slide only on inner wrapper so transforms never fight ScrollTrigger pin on `.stick-button-item`. */
  const canSlide = motionEl !== stickButtons;
  const STICK_BTN_FADE_SEC = 0.4;
  const STICK_BTN_SLIDE_PX = 28;
  let fadeTween = null;
  /** Last visibility target we animated to; avoids restart / flicker on duplicate callbacks. */
  let motionTargetActive;

  const applyMotionEndState = (active) => {
    if (canSlide) {
      gsap.set(motionEl, {
        autoAlpha: active ? 1 : 0,
        y: active ? 0 : STICK_BTN_SLIDE_PX,
      });
    } else {
      gsap.set(motionEl, { autoAlpha: active ? 1 : 0 });
    }
  };

  const tweenMotionTo = (active) => {
    fadeTween?.kill();
    if (canSlide) {
      if (active) {
        fadeTween = gsap.fromTo(
          motionEl,
          { autoAlpha: 0, y: STICK_BTN_SLIDE_PX },
          {
            autoAlpha: 1,
            y: 0,
            duration: STICK_BTN_FADE_SEC,
            ease: "power2.out",
            overwrite: "auto",
          }
        );
      } else {
        fadeTween = gsap.to(motionEl, {
          autoAlpha: 0,
          y: STICK_BTN_SLIDE_PX,
          duration: STICK_BTN_FADE_SEC,
          ease: "power2.in",
          overwrite: "auto",
        });
      }
    } else {
      fadeTween = gsap.to(motionEl, {
        autoAlpha: active ? 1 : 0,
        duration: STICK_BTN_FADE_SEC,
        ease: active ? "power2.out" : "power2.in",
        overwrite: "auto",
      });
    }
  };

  const syncFromTrigger = (self, snap) => {
    applyPinnedLayer(self, stickButtons, UI_TOP_LAYER);
    const active = self.isActive;
    if (snap) {
      fadeTween?.kill();
      motionTargetActive = active;
      applyMotionEndState(active);
      return;
    }
    if (motionTargetActive === active) return;
    motionTargetActive = active;
    tweenMotionTo(active);
  };

  gsap.set(stickButtons, { zIndex: UI_TOP_LAYER });
  if (canSlide) {
    gsap.set(motionEl, { autoAlpha: 0, y: STICK_BTN_SLIDE_PX });
  } else {
    gsap.set(motionEl, { autoAlpha: 0 });
  }

  const st = createTrigger({
    trigger: stickButtons,
    start: "bottom bottom-=12",
    endTrigger: sectionWrap,
    end: "bottom top",
    pin: true,
    pinSpacing: false,
    onRefresh(self) {
      syncFromTrigger(self, true);
    },
    onToggle(self) {
      syncFromTrigger(self, false);
    },
  });

  motionTargetActive = st.isActive;
  applyPinnedLayer(st, stickButtons, UI_TOP_LAYER);
  if (st.isActive) {
    tweenMotionTo(true);
  } else {
    applyMotionEndState(false);
  }

  return () => {
    fadeTween?.kill();
    st.kill();
    gsap.set(stickButtons, { clearProps: "zIndex" });
    gsap.set(motionEl, { clearProps: "opacity,visibility,transform" });
    if (stickButtonsWrap) gsap.set(stickButtonsWrap, { clearProps: "position,zIndex" });
  };
});

/* Gradient strip pin: desktop xl only */
mm.add("(min-width: 1200px)", () => {
  if (!serviceGradient || !sectionWrap) return () => {};
  gsap.set(serviceGradient, { zIndex: UI_TOP_LAYER - 1 });

  const st = createTrigger({
    trigger: serviceGradient,
    start: "bottom bottom-=90",
    endTrigger: sectionWrap,
    end: "bottom top",
    pin: true,
    pinSpacing: false,
    onRefresh(self) {
      applyPinnedLayer(self, serviceGradient, UI_TOP_LAYER - 1);
    },
    onToggle(self) {
      applyPinnedLayer(self, serviceGradient, UI_TOP_LAYER - 1);
    },
  });
  applyPinnedLayer(st, serviceGradient, UI_TOP_LAYER - 1);

  return () => {
    st.kill();
    gsap.set(serviceGradient, { clearProps: "zIndex" });
  };
});

/* Service cards stack pin/transform: desktop only */
mm.add("(min-width: 1024px)", () => {
  const created = [];

  cardsWrappers.forEach((wrapper, i) => {
    const card = wrapper.querySelector(".service-inner");
    const cardBg = wrapper.querySelector(".service-stack-bg");
    if (!card) return;

    // Natural stack order: each next card stays above the previous one.
    const cardLayer = CARD_STACK_BASE_Z + i + 1;
    gsap.set(wrapper, { zIndex: cardLayer });
    gsap.set(card, { autoAlpha: 1, transformOrigin: "top center" });

    const stBase = {
      trigger: wrapper,
      start: "top top+=100",
      endTrigger: sectionWrap || wrapper,
      end: sectionWrap ? "bottom top" : "bottom top",
      pin: wrapper,
      pinSpacing: false,
      id: `service-card-${i + 1}`,
    };

    if (isLastCard(i)) {
      const tw = gsap.to(card, {
        scale: 1,
        rotationX: 0,
        y: 0,
        transformOrigin: "top center",
        ease: "none",
        paused: true,
      });

      created.push(
        createTrigger({
          ...stBase,
          onRefresh(self) {
            gsap.set(wrapper, { zIndex: cardLayer });
            if (self.pin) gsap.set(self.pin, { zIndex: cardLayer });
          },
          onToggle(self) {
            gsap.set(wrapper, { zIndex: cardLayer });
            if (self.pin) gsap.set(self.pin, { zIndex: cardLayer });
          },
          onUpdate(self) {
            tw.progress(self.progress);
          },
        })
      );
    } else {
      const tl = gsap.timeline({ paused: true });
      tl.fromTo(
        card,
        { scale: 1, rotationX: 0, y: 0, transformOrigin: "top center" },
        {
          scale: 0.75,
          rotationX: -10,
          y: 40,
          transformOrigin: "top center",
          ease: "none",
        },
        0
      );

      if (cardBg) {
        tl.fromTo(
          cardBg,
          { backgroundColor: CARD_BG_FROM },
          { backgroundColor: CARD_BG_TO, ease: "none", duration: 0.05 },
          0
        );
      }

      created.push(
        createTrigger({
          ...stBase,
          onRefresh(self) {
            gsap.set(wrapper, { zIndex: cardLayer });
            if (self.pin) gsap.set(self.pin, { zIndex: cardLayer });
          },
          onToggle(self) {
            gsap.set(wrapper, { zIndex: cardLayer });
            if (self.pin) gsap.set(self.pin, { zIndex: cardLayer });
          },
          onUpdate(self) {
            tl.progress(self.progress);
          },
        })
      );
    }
  });

  return () => {
    created.forEach((st) => st.kill());
    cardsWrappers.forEach((wrapper) => {
      gsap.set(wrapper, { clearProps: "zIndex" });
      const card = wrapper.querySelector(".service-inner");
      if (card) gsap.set(card, { clearProps: "opacity,visibility,transformOrigin" });
    });
  };
});

/*
 * <=1023px only: when logo marquee enters view, pin only Services section above it.
 */
mm.add("(max-width: 1023px)", () => {
  const autoSliderSection = document.querySelector(".global_auto_slider_section");
  const serviceSection1 = document.querySelector(".service-section-1");
  if (!autoSliderSection || !serviceSection1) return () => {};

  const st = createTrigger({
    trigger: autoSliderSection,
    start: "top bottom",
    end: "top top",
    pin: serviceSection1,
    pinSpacing: false,
  });

  return () => st.kill();
});

(function initServiceWaveCanvas() {
  const SEL = ".js-service-wave-canvas";

  function startInstance(canvas, phase) {
    if (!canvas.parentElement || canvas.dataset.waveInited === "1") return;
    canvas.dataset.waveInited = "1";
    if (typeof THREE === "undefined") return;

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, 1, 0.1, 100);
    camera.position.set(0, 2.5, 6);
    camera.lookAt(0, 0, 0);

    function resize() {
      const w = canvas.parentElement.clientWidth;
      const h = canvas.parentElement.clientHeight;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }
    resize();
    window.addEventListener("resize", resize);

    const SEG = 96;
    const geo = new THREE.PlaneGeometry(14, 10, SEG, SEG);
    geo.rotateX(-Math.PI / 2);

    const pos = geo.attributes.position;
    const origY = new Float32Array(pos.count);
    for (let i = 0; i < pos.count; i++) origY[i] = pos.getY(i);

    const colors = new Float32Array(pos.count * 3);
    geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));

    const mat = new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide });
    const waveMesh = new THREE.Mesh(geo, mat);
    scene.add(waveMesh);

    /* Primary #325AFB (50,90,251): shade by height using one RGB ratio so hue never drifts from the brand swatch. */
    const BR = 50 / 255;
    const BG = 90 / 255;
    const BB = 251 / 255;
    const SHADE_MIN = 0.86;
    const SHADE_MAX = 1;

    /** Lower = slower wave motion (was 0.012). */
    const WAVE_TIME_STEP = 0.005;
    const wrap = canvas.parentElement;
    let waveFadeStarted = false;
    let waveInView = true;
    const waveIo = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        waveInView = !!(e && e.isIntersecting);
      },
      { root: null, rootMargin: "80px 0px", threshold: 0 }
    );
    waveIo.observe(canvas);

    let t = phase;
    function animate() {
      requestAnimationFrame(animate);
      if (!waveInView) return;
      t += WAVE_TIME_STEP;

      const posArr = geo.attributes.position.array;
      const colArr = geo.attributes.color.array;
      const heights = new Float32Array(pos.count);
      let minH = Infinity;
      let maxH = -Infinity;

      for (let i = 0; i < pos.count; i++) {
        const x = posArr[i * 3];
        const z = posArr[i * 3 + 2];
        const h =
          Math.sin(x * 0.6 + t * 1.1) * 0.55 +
          Math.sin(z * 0.5 + t * 0.8) * 0.45 +
          Math.sin((x + z) * 0.4 + t * 0.6) * 0.35 +
          Math.cos(x * 0.3 - z * 0.5 + t * 0.9) * 0.25 +
          Math.sin(x * 1.1 + z * 0.7 + t * 0.5) * 0.2;
        heights[i] = h;
        posArr[i * 3 + 1] = origY[i] + h;
        if (h < minH) minH = h;
        if (h > maxH) maxH = h;
      }

      const range = maxH - minH || 1;
      for (let i = 0; i < pos.count; i++) {
        const n = (heights[i] - minH) / range;
        const shade = SHADE_MIN + n * (SHADE_MAX - SHADE_MIN);
        colArr[i * 3] = BR * shade;
        colArr[i * 3 + 1] = BG * shade;
        colArr[i * 3 + 2] = Math.min(1, BB * shade);
      }

      geo.attributes.position.needsUpdate = true;
      geo.attributes.color.needsUpdate = true;

      camera.position.x = Math.sin(t * 0.08) * 0.8;
      camera.position.y = 2.2 + Math.cos(t * 0.06) * 0.3;
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
      if (!waveFadeStarted && wrap) {
        waveFadeStarted = true;
        requestAnimationFrame(() => wrap.classList.add("js-service-wave-visible"));
      }
    }

    animate();
  }

  function bootAll() {
    if (typeof THREE === "undefined") return;
    document.querySelectorAll(SEL).forEach((el, i) => startInstance(el, i * 2.7));
  }

  if (!document.querySelectorAll(SEL).length) return;

  if (typeof THREE !== "undefined") {
    bootAll();
  } else {
    let tag = document.querySelector("script[data-service-wave-three]");
    if (tag) {
      tag.addEventListener("load", bootAll);
      if (typeof THREE !== "undefined") bootAll();
    } else {
      tag = document.createElement("script");
      tag.src = "https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js";
      tag.async = true;
      tag.dataset.serviceWaveThree = "1";
      tag.onload = bootAll;
      document.head.appendChild(tag);
    }
  }
})();

// Lenis + ScrollTrigger sync refresh for services page.
function refreshServiceScrollTriggers() {
  const lenisInstance = window.__lenisInstance || window.lenis;
  if (lenisInstance && typeof lenisInstance.resize === "function") {
    lenisInstance.resize();
  }
  ScrollTrigger.sort();
  ScrollTrigger.refresh();
}

requestAnimationFrame(refreshServiceScrollTriggers);
window.addEventListener("load", refreshServiceScrollTriggers);
setTimeout(refreshServiceScrollTriggers, 300);